/**
 * Capital Domain
 * Store, handlers, and service methods for debt and equity actions.
 *
 * An order the server refuses comes back as a toast, in the player's language
 * where the reason is one the game knows, and in the server's own words where
 * it is not.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import { sendGatedCommand } from './command-gate';
import { getCurrentTeam } from './current-team';
import { showErrorToast } from './toasts';
import i18n from '../i18n/config';
import { formatCash } from '../utils/format';
import { budgetBlock, refusalKey, type OrderBlock, type OrderRefusal } from './order-block';
import { sendToMatch } from '../services/nakama/match-send';

/**
 * A team's capital as every player is shown it: the server's TeamCapitalView
 * (finance/capital-state.go), which the server's TeamCapital embeds beside what
 * the team paid for its rivals' stakes, kept from the wire.
 */
export interface TeamCapital {
  team_id: number;
  team_percent: number;
  public_percent: number;
  rival_percent: Record<number, number>;
  debt: number;
  last_debt_rate_annual: number;
  company_value: number;
}

export interface DebtQuote {
  team_id: number;
  as_of_quarter: number;
  trailing_revenue: number;
  strength: number;
  debt_capacity_ratio: number;
  debt_capacity: number;
  current_debt: number;
  available: number;
  risk_free_rate: number;
  risk_premium: number;
  all_in_annual_rate: number;
}

export interface DebtExecution {
  order: {
    team_id: number;
    action: string;
    amount: number;
    as_of_quarter: number;
    reason?: string;
  };
  all_in_annual_rate: number;
  interest_this_quarter: number;
}

export interface EquityQuote {
  team_id: number;
  as_of_quarter: number;
  ttm_revenue: number;
  strength: number;
  company_value: number;
  price_per_percent: number;
}

interface OwnershipSnapshot {
  team_id: number;
  team_percent: number;
  public_percent: number;
  rival_percent: Record<number, number>;
}

export interface EquityExecution {
  order: {
    team_id: number;
    action: string;
    percent: number;
    as_of_quarter: number;
    actor_team_id: number;
    reason?: string;
  };
  price_per_percent: number;
  total_cash: number;
  cash_delta: number;
  ownership_after: OwnershipSnapshot;
}

/**
 * Rounds as the printed figure does: Intl rounds the decimal a value reads as
 * (1.45 → 1.5), where toFixed rounds the binary one below it (1.4).
 */
const TENTHS = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false });

/**
 * A company value as airlines are compared by it: in the tenths of a million
 * the game prints it to (formatCash), so two airlines whose values print alike
 * share a place and neither leads the other. The rail's rank, the Rivals board
 * and its sentence all compare through here, and a lead or gap between two of
 * these, over ten, is the difference the printed values show.
 */
export function valueInTenths(millions: number): number {
  return Math.round(Number(TENTHS.format(millions)) * 10);
}

/**
 * Where a team stands among all teams by market value: 1 is the most valuable,
 * and teams whose values print alike share a place. Null until the team's
 * value is known.
 */
export function marketValueRank(capitalByTeam: Record<number, TeamCapital>, teamId: number): { rank: number; of: number } | null {
  const own = capitalByTeam[teamId];
  if (!own) return null;
  const mine = valueInTenths(own.company_value);
  const values = Object.values(capitalByTeam).map((capital) => valueInTenths(capital.company_value));
  return { rank: 1 + values.filter((value) => value > mine).length, of: values.length };
}

interface CapitalState {
  capitalByTeam: Record<number, TeamCapital>;
  debtQuote?: DebtQuote;
  equityQuote?: EquityQuote;
  /** Your team's last borrow or repay. */
  lastDebtExecution?: DebtExecution;
  /** Your team's last issue or buyback of its own shares. */
  lastEquityExecution?: EquityExecution;
  setCapitalState: (capital: Record<number, TeamCapital>) => void;
  setCapital: (capital: TeamCapital) => void;
  setDebtQuote: (quote: DebtQuote) => void;
  setEquityQuote: (quote: EquityQuote) => void;
  setDebtExecution: (execution: DebtExecution) => void;
  setEquityExecution: (execution: EquityExecution) => void;
  reset: () => void;
}

const initialState = {
  capitalByTeam: {},
} as const;

export const useCapitalStore = create<CapitalState>()(
  devtools((set) => ({
    ...initialState,

    setCapitalState: (capital) => {
      set({ capitalByTeam: { ...capital } }, false, 'setCapitalState');
    },

    setCapital: (capital) => {
      set(
        (state) => ({
          capitalByTeam: {
            ...state.capitalByTeam,
            [capital.team_id]: capital,
          },
        }),
        false,
        'setCapital'
      );
    },

    setDebtQuote: (quote) => {
      set({ debtQuote: quote }, false, 'setDebtQuote');
    },

    setEquityQuote: (quote) => {
      set({ equityQuote: quote }, false, 'setEquityQuote');
    },

    setDebtExecution: (execution) => {
      set({ lastDebtExecution: execution }, false, 'setDebtExecution');
    },

    setEquityExecution: (execution) => {
      set({ lastEquityExecution: execution }, false, 'setEquityExecution');
    },

    reset: () => {
      set({ ...initialState, debtQuote: undefined, equityQuote: undefined, lastDebtExecution: undefined, lastEquityExecution: undefined }, false, 'resetCapital');
    },
  }))
);

export function handleDebtQuoteSuccess(data: { quote: DebtQuote }): void {
  if (!data?.quote) {
    return;
  }
  useCapitalStore.getState().setDebtQuote(data.quote);
}

/**
 * A borrow or a repay went through. Every team hears every order, so the
 * capital is kept whoever it belongs to, and the execution only when it is
 * your team's: the Debt tile's last action is your own team's, not a rival's.
 */
export function handleDebtOrderSuccess(data: { capital?: TeamCapital; execution?: DebtExecution }): void {
  if (data?.capital) {
    useCapitalStore.getState().setCapital(data.capital);
  }
  if (data?.execution && data.execution.order.team_id === getCurrentTeam()?.team_id) {
    useCapitalStore.getState().setDebtExecution(data.execution);
  }
}

export function handleEquityQuoteSuccess(data: { quote: EquityQuote }): void {
  if (!data?.quote) {
    return;
  }
  useCapitalStore.getState().setEquityQuote(data.quote);
}

/**
 * An equity order went through. The execution is kept only for your team's
 * own issue or buyback — the order is on your shares and by your team — so a
 * rival buying into you, or your team trading a rival's shares, does not
 * become the Equity tile's last action.
 */
export function handleEquityOrderSuccess(data: { capital?: TeamCapital; execution?: EquityExecution }): void {
  if (data?.capital) {
    useCapitalStore.getState().setCapital(data.capital);
  }
  const yours = getCurrentTeam()?.team_id;
  const order = data?.execution?.order;
  if (data?.execution && order && order.team_id === yours && order.actor_team_id === yours) {
    useCapitalStore.getState().setEquityExecution(data.execution);
  }
}

// ==================== ORDER RULES ====================

/** Borrowing moves in tenths of a million. */
const STEPS_PER_MILLION = 10;
export const BORROW_STEP = 1 / STEPS_PER_MILLION;

/** Orders on shares are one percent at a time. */
export const ONE_PERCENT = 1;

/*
 * Each rule below says why an order cannot go now, as an OrderBlock
 * (order-block.ts): the key that says so in the player's language, and the
 * figures it names, written as the game writes money. Each predicts a check
 * the server makes, so the button rests with the reason instead of the server
 * refusing the order; a step's spending lock is SpendButton's, ahead of all.
 */

/** Room for the float error in figures held in millions. */
const EPSILON = 1e-9;

const isAmount = (amount: number) => Number.isFinite(amount) && amount > 0;

/** What the bank lends now, rounded down to a whole step; nothing before a quote. */
export function borrowable(quote: DebtQuote | undefined): number | undefined {
  return quote ? Math.floor(quote.available * STEPS_PER_MILLION + EPSILON) / STEPS_PER_MILLION : undefined;
}

/** An amount that is not in whole steps of BORROW_STEP. */
export function offStep(amount: number): boolean {
  return isAmount(amount) && Math.abs(amount * STEPS_PER_MILLION - Math.round(amount * STEPS_PER_MILLION)) > 1e-6;
}

/** Why Borrow cannot go: an amount above zero, in whole steps, no more than the bank lends (banker.go). */
export function borrowBlock(amount: number, available: number | undefined): OrderBlock | null {
  if (!isAmount(amount)) return { key: 'capital.reason.noAmount' };
  if (offStep(amount)) return { key: 'capital.reason.borrowSteps', values: { step: formatCash(BORROW_STEP) } };
  if (available !== undefined && amount > available + EPSILON) {
    return { key: 'capital.reason.overAvailable', values: { available: formatCash(available) } };
  }
  return null;
}

/**
 * Why Repay cannot go: the team owes something; an amount above zero and no
 * more than the debt (banker.go); the team's cash to pay it; and your budget,
 * which repaying comes out of (capital-sync.go). A figure not known yet
 * predicts nothing, and the server decides.
 */
export function repayBlock(amount: number, { debt, cash, budget }: { debt: number; cash?: number; budget?: number }): OrderBlock | null {
  if (!(debt > 0)) return { key: 'capital.reason.noDebt' };
  if (!isAmount(amount)) return { key: 'capital.reason.noAmount' };
  if (amount > debt + EPSILON) return { key: 'capital.reason.overDebt', values: { debt: formatCash(debt) } };
  if (cash !== undefined && amount > cash + EPSILON) return { key: 'capital.refusal.cashToRepay' };
  return budgetBlock(budget, amount);
}

/** Why Issue 1% cannot go: the team has none of its own shares left to sell (broker.go). */
export function issueBlock(capital: TeamCapital | undefined): OrderBlock | null {
  return capital && capital.team_percent < ONE_PERCENT ? { key: 'capital.reason.noTeamShares' } : null;
}

/**
 * Why Buyback 1% cannot go: no public shares to buy back (broker.go), or the
 * team's cash or your budget short of its price — a buyback comes out of both
 * (capital-sync.go).
 */
export function buybackBlock(
  capital: TeamCapital | undefined,
  { price, cash, budget }: { price?: number; cash?: number; budget?: number },
): OrderBlock | null {
  if (capital && capital.public_percent < ONE_PERCENT) return { key: 'capital.reason.noPublicShares' };
  if (price === undefined) return null;
  const cost = price * ONE_PERCENT;
  if (cash !== undefined && cost > cash + EPSILON) return { key: 'capital.refusal.cashToBuyBack' };
  return budgetBlock(budget, cost);
}

/**
 * Why Buy 1% of a rival cannot go: none of its shares are on the market
 * (broker.go). Its price is the server's alone — no quote reaches the client —
 * so cash and budget are the server's to check.
 */
export function rivalBuyBlock(publicPercent: number, airline: string): OrderBlock | null {
  return publicPercent < ONE_PERCENT ? { key: 'capital.reason.noFloat', values: { airline } } : null;
}

/** Why Sell 1% of a rival cannot go: your team holds none of it (broker.go). */
export function rivalSellBlock(held: number, airline: string): OrderBlock | null {
  return held < ONE_PERCENT ? { key: 'capital.reason.noneHeld', values: { airline } } : null;
}

// ==================== REFUSALS ====================

/**
 * The reasons the server refuses a capital order for, where a player can meet
 * them — capital-sync.go, finance/refusal.go and spending-lock.go send each as
 * a code beside the English — by the key that says each in the player's
 * language. `budget` carries the order's cost and the player's budget, in
 * millions. A Go test reads this table, so the two sides name the same codes.
 */
const CAPITAL_REFUSALS = {
  budget: 'capital.refusal.budget',
  cash_locked: 'common.cashLocked',
  cash_to_repay: 'capital.refusal.cashToRepay',
  cash_to_buy_back: 'capital.refusal.cashToBuyBack',
  cash_to_buy_shares: 'capital.refusal.cashToBuyShares',
  over_capacity: 'capital.refusal.overCapacity',
  over_debt: 'capital.refusal.overDebt',
  over_team_shares: 'capital.refusal.overTeamShares',
  over_public_shares: 'capital.refusal.overPublicShares',
  over_holdings: 'capital.refusal.overHoldings',
} as const;

type CapitalRefusal =
  | { kind: 'budget'; need: number; have: number }
  | { kind: 'known'; key: string }
  | { kind: 'unknown'; text: string };

/**
 * What a capital refusal says: a reason the game knows, by its key; the budget
 * refusal with its figures, so they print in the player's number style; or,
 * with no reason the client knows, the server's words as they came.
 */
export function readCapitalRefusal(failure: OrderRefusal): CapitalRefusal {
  if (failure.reason === 'budget' && typeof failure.need === 'number' && typeof failure.have === 'number') {
    return { kind: 'budget', need: failure.need, have: failure.have };
  }
  const key = refusalKey(CAPITAL_REFUSALS, failure.reason);
  if (key !== undefined && failure.reason !== 'budget') {
    return { kind: 'known', key };
  }
  return { kind: 'unknown', text: failure.error.trim() };
}

/** A quote or an order the server refused: a toast, where the game's passing failures go. */
export function handleCapitalError(failure: OrderRefusal): void {
  if (!failure.error) {
    return;
  }
  console.warn('[CapitalHandler] Refused:', failure.reason ?? '', failure.error);
  const refusal = readCapitalRefusal(failure);
  switch (refusal.kind) {
    case 'budget':
      showErrorToast(
        i18n.t(CAPITAL_REFUSALS.budget, { need: formatCash(refusal.need), have: formatCash(refusal.have) }),
        i18n.t('capital.refusal.budgetDetail'),
      );
      return;
    case 'known':
      showErrorToast(i18n.t(refusal.key));
      return;
    case 'unknown':
      showErrorToast(refusal.text);
  }
}

/** The capital state as the server sends it (capital-sync.go): the router's guard returns this. */
export interface CapitalStateMessage {
  type: typeof MessageType.MessageTypeCapitalState;
  capital: Record<number, TeamCapital>;
}

export function handleCapitalStateSync(data: CapitalStateMessage): void {
  useCapitalStore.getState().setCapitalState(data.capital);
}

/** Asks for the team's debt quote; the tile keeps the last one until it comes. */
export async function requestDebtQuote(): Promise<void> {
  await sendToMatch(OpCode.OpCodeCapitalDebtQuote, { type: MessageType.MessageTypeCapitalDebtQuote }, 'a debt quote request');
}

/** Asks for the team's equity quote; the tile keeps the last one until it comes. */
export async function requestEquityQuote(): Promise<void> {
  await sendToMatch(OpCode.OpCodeCapitalEquityQuote, { type: MessageType.MessageTypeCapitalEquityQuote }, 'an equity quote request');
}

/**
 * Borrow or repay. True when the order went; false when it did not — the
 * command gate turned it away for another on its way, or it could not be
 * sent, which a toast says as a refusal — so nothing will answer.
 */
export async function submitDebtOrder(action: 'borrow' | 'repay', amount: number, reason?: string): Promise<boolean> {
  const message = {
    type: MessageType.MessageTypeCapitalDebtOrder,
    action,
    amount,
    reason,
  };

  return sendGatedCommand(OpCode.OpCodeCapitalDebtOrder, JSON.stringify(message), { notSent: handleCapitalError });
}

/** Issue or buy back the team's own shares; true when the order went, as submitDebtOrder. */
export async function submitEquityOrder(action: 'issue' | 'buyback', percent: number, reason?: string): Promise<boolean> {
  const message = {
    type: MessageType.MessageTypeCapitalEquityOrder,
    action,
    percent,
    reason,
  };

  return sendGatedCommand(OpCode.OpCodeCapitalEquityOrder, JSON.stringify(message), { notSent: handleCapitalError });
}

/** Buy or sell 1% of a rival; true when the order went, as submitDebtOrder. */
export async function submitRivalEquityOrder(
  action: 'rival_buy' | 'rival_sell',
  targetTeamId: number,
  percent: number,
  reason?: string
): Promise<boolean> {
  const message = {
    type: MessageType.MessageTypeCapitalEquityOrder,
    action,
    percent,
    target_team_id: targetTeamId,
    reason,
  };

  return sendGatedCommand(OpCode.OpCodeCapitalEquityOrder, JSON.stringify(message), { notSent: handleCapitalError });
}
